import React from 'react'
import classnames from 'classnames/bind'
import { abbreviate } from '@pqt/abbreviate'
import prettysize from 'prettysize'
import type { AutomationTarget } from '../../../types'
import Icon from '../icon'
import Text from '../text'
import styles from './styles.scss'
import { colorVars } from '../../../constants'

const cx = classnames.bind(styles)

type IconColor = 'info' | 'success' | 'caution' | 'error' | 'purple' | 'gray'
type Datatype = 'count' | 'percentage' | 'time' | 'memory' | 'currency'
type Size = 'default' | 'compact'

export interface StatisticProps extends AutomationTarget {
  /** FontAwesome icon name (e.g. 'phone', 'dollar-sign'). If omitted, no icon is shown. */
  iconName?: string
  /** Color role for the icon circle */
  accentColor?: IconColor
  /** Determines how the value is formatted */
  datatype: Datatype
  /** Numeric value to display. null renders nullValue string. */
  value: number | null
  /** Number of decimal places in formatted output */
  decimalPlaces?: number
  /** Descriptive label above the value */
  label: string
  /** Display size variant */
  size?: Size
  /** String to display when value is null */
  nullValue?: string
  /** Additional className */
  className?: string
  /** Allows to set a custom background color to the component */
  backgroundColor?: keyof typeof colorVars
}

/**
 * Maps accentColor to the color name used by the Icon component.
 * Icon component uses colorShortToLong internally, so we pass the base color name.
 */
const accentColorMap: Record<IconColor, string> = {
  info: 'infoActiveMedium',
  success: 'successActiveMedium',
  caution: 'cautionActiveMedium',
  error: 'errorActiveMedium',
  purple: 'purpleActiveMedium',
  gray: 'grayMedium',
}

/**
 * Format a time value from milliseconds into the two most significant units.
 * e.g. 1234000 → "20m 34s", 3500 → "3s 500ms"
 */
function formatTime(ms: number): string {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`
  }

  const totalSeconds = Math.floor(ms / 1000)
  const remainderMs = Math.round(ms % 1000)

  if (totalSeconds < 60) {
    if (remainderMs > 0) {
      return `${totalSeconds}s ${remainderMs}ms`
    }
    return `${totalSeconds}s`
  }

  const totalMinutes = Math.floor(totalSeconds / 60)
  const remainderSeconds = totalSeconds % 60

  if (totalMinutes < 60) {
    if (remainderSeconds > 0) {
      return `${totalMinutes}m ${remainderSeconds}s`
    }
    return `${totalMinutes}m`
  }

  const totalHours = Math.floor(totalMinutes / 60)
  const remainderMinutes = totalMinutes % 60

  if (totalHours < 24) {
    if (remainderMinutes > 0) {
      return `${totalHours}h ${remainderMinutes}m`
    }
    return `${totalHours}h`
  }

  const totalDays = Math.floor(totalHours / 24)
  const remainderHours = totalHours % 24

  if (remainderHours > 0) {
    return `${totalDays}d ${remainderHours}h`
  }
  return `${totalDays}d`
}

/**
 * Format a currency value with $ prefix, commas, and decimal places. Never abbreviated.
 */
function formatCurrency(value: number, decimals: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

/**
 * Format a count value. Abbreviates > 999 with K/M/B suffixes.
 */
function formatCount(value: number, decimals: number): string {
  if (Math.abs(value) > 999) {
    return abbreviate(value, decimals)
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })
}

/**
 * Format a percentage value. Appends % suffix.
 */
function formatPercentage(value: number, decimals: number): string {
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`
}

/**
 * Format a memory value from bytes. Uses prettysize with space between number and unit.
 * Replaces "Bytes" with "B" for brevity.
 */
function formatMemory(bytes: number, decimals: number): string {
  const result = prettysize(bytes, false, false, decimals) as string
  return result.replace('Bytes', 'B')
}

/**
 * Master formatting dispatcher.
 */
function formatStatValue(
  value: number,
  datatype: Datatype,
  decimalPlaces: number
): string {
  switch (datatype) {
    case 'count':
      return formatCount(value, decimalPlaces)
    case 'currency':
      return formatCurrency(value, decimalPlaces)
    case 'percentage':
      return formatPercentage(value, decimalPlaces)
    case 'time':
      return formatTime(value)
    case 'memory':
      return formatMemory(value, decimalPlaces)
    default:
      return String(value)
  }
}

const Statistic: React.FC<StatisticProps> = ({
  iconName,
  accentColor = 'gray',
  datatype,
  value,
  decimalPlaces = 0,
  label,
  size = 'default',
  nullValue = '--',
  className,
  elmId = 'statistic',
  backgroundColor,
}) => {
  const formattedValue =
    value === null ? nullValue : formatStatValue(value, datatype, decimalPlaces)

  const ariaLabel = `${label}: ${formattedValue}`
  const showIcon = !!iconName

  return (
    <div
      className={cx(
        'statistic',
        `statistic-${size}`,
        { 'no-icon': !showIcon },
        !showIcon && `gradient-${accentColor}`,
        className
      )}
      role='figure'
      aria-label={ariaLabel}
      data-elm-id={elmId}
      style={
        backgroundColor
          ? { backgroundColor: colorVars[backgroundColor] }
          : undefined
      }
    >
      {showIcon && (
        <div className={cx('icon-circle', `icon-circle-${accentColor}`)}>
          <Icon
            name={iconName}
            color={accentColorMap[accentColor]}
            iconStyle='solid'
            size={size === 'compact' ? 'medium' : 'large'}
            fitted
          />
        </div>
      )}
      <div className={cx('content')}>
        <Text
          size={size === 'compact' ? 'small' : 'big'}
          color='grayDark'
          weight='black'
          elmId={`${elmId}-value`}
        >
          {formattedValue}
        </Text>
        <Text
          size={size === 'compact' ? 'tiny' : 'small'}
          color='gray'
          weight='normal'
          elmId={`${elmId}-label`}
        >
          {label}
        </Text>
      </div>
    </div>
  )
}

export default Statistic
